/**
 * One past paper, in the platform console (/admin/papers/:id).
 *
 * Two shapes of the same editor, because a paper has two lives:
 *
 *  - A DRAFT (`/admin/papers/:id`): every field of every row is editable —
 *    question, options, the correct choice, marks, time, TEAM, video and the
 *    sub-topic — and nothing reaches a student until Publish. A row can be
 *    re-tagged with AI or have its TEAM drafted with AI; both stay editable.
 *  - A PUBLISHED paper (`?kind=paper`), seeded or published here: its help and
 *    its tag stay editable question by question, and its whole help can be
 *    downloaded and uploaded as one `cwy.paperhelp/1` file. Its key is held
 *    still: changing a question, its options or its answer needs an explicit
 *    "Change answer key", because sittings already recorded are not re-marked.
 *
 * Each question is the teacher's own `PaperItemEditor`, so a row means the same
 * thing in every console.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PAPER_HELP_MAX_BYTES,
  TEAM_PART_LABEL,
  gradeLabel,
  paperSeconds,
  type ConceptOption,
  type CustomPaperItem,
  type PaperHelpContent,
  type PaperHelpUploadResult,
  type PlatformPaperDraftDTO,
  type PlatformTagResult,
  type PublishedPaperDTO,
  type PublishedPaperItem,
  type TeamPart,
} from '@cwy/config/contracts';
import { apiDelete, apiErrorMessage, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { Button, Card, Field, Input, StudentPage, StudentPageHeader } from '@/components/ds';
import { QueryState } from '@/components/admin/QueryState';
import { PaperItemEditor } from '@/components/teacher/paper/PaperItemEditor';
import {
  defaultItem,
  fromServer,
  serializeItems,
  type PaperQuestionType,
} from '@/components/teacher/paper/editorItems';
import { StatePill } from './PlatformPapersPage';

const LIST_KEY = ['platform', 'papers'] as const;

function useConcepts(subjectId: string | null | undefined) {
  const query = useQuery({
    queryKey: ['platform', 'concepts', subjectId],
    enabled: Boolean(subjectId),
    queryFn: () => apiGet<ConceptOption[]>(`/platform/papers/concepts?subjectId=${subjectId}`),
  });
  return useMemo(() => query.data ?? [], [query.data]);
}

/** What a tag result says on the page. */
function tagMessage(index: number, result: PlatformTagResult): string {
  if (!result.tag)
    return `Q${index}: ${result.note ?? 'No sub-topic found — choose one with ✎ Change.'}`;
  const where = `${result.tag.name} · ${result.tag.chapterName}`;
  const how = result.by === 'ai' ? 'AI filed it under' : 'Matched on its words to';
  return `Q${index}: ${how} ${where}${result.tag.sure ? '.' : ' — a best guess, check it.'}${result.note && result.by !== 'ai' ? ` ${result.note}` : ''}`;
}

export function PlatformPaperEditorPage() {
  const { id = '' } = useParams();
  const [search] = useSearchParams();
  return search.get('kind') === 'paper' ? (
    <PublishedEditor paperId={id} />
  ) : (
    <DraftEditor draftId={id} />
  );
}

const crumbs = (title: string) => [{ label: 'Past papers', to: '/admin/papers' }, { label: title }];

// ─────────────────────────── a draft ───────────────────────────

function DraftEditor({ draftId }: { draftId: string }) {
  const draft = useQuery({
    queryKey: ['platform', 'paper-draft', draftId],
    queryFn: () => apiGet<PlatformPaperDraftDTO>(`/platform/papers/drafts/${draftId}`),
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 3_000 : false),
  });
  return (
    <StudentPage>
      <QueryState isPending={draft.isPending} error={draft.error}>
        {draft.data && <DraftReview key={draft.data.id} detail={draft.data} />}
      </QueryState>
    </StudentPage>
  );
}

function DraftReview({ detail }: { detail: PlatformPaperDraftDTO }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<CustomPaperItem[]>(() => fromServer(detail.items));
  const [title, setTitle] = useState(detail.title);
  const [year, setYear] = useState(String(detail.examYear ?? ''));
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const concepts = useConcepts(detail.subjectId);

  // Re-seed from a refetch (the PDF finished reading) — local state only, never a write.
  useEffect(() => {
    if (!dirty) {
      setItems(fromServer(detail.items));
      setTitle(detail.title);
      setYear(String(detail.examYear ?? ''));
    }
  }, [detail, dirty]);

  const key = ['platform', 'paper-draft', detail.id];
  const editable = ['draft', 'review', 'done', 'failed'].includes(detail.status);
  // Per-row AI and publish need a paper that has been read (or typed).
  const reviewable = ['draft', 'review', 'done'].includes(detail.status);

  const body = () => ({
    title: title.trim() || detail.title,
    ...(/^\d{4}$/.test(year) ? { examYear: Number(year) } : {}),
    items: serializeItems(items),
  });

  const accept = (saved: PlatformPaperDraftDTO) => {
    queryClient.setQueryData(key, saved);
    setItems(fromServer(saved.items));
    setDirty(false);
    void queryClient.invalidateQueries({ queryKey: LIST_KEY });
  };

  /** Anything unsaved goes up first, so the server reads what is on the screen. */
  const flush = async () => {
    if (!dirty) return;
    accept(await apiPatch<PlatformPaperDraftDTO>(`/platform/papers/drafts/${detail.id}`, body()));
  };

  const fail = (err: unknown) => {
    setError(apiErrorMessage(err));
    setMessage('');
  };

  const save = useMutation({
    mutationFn: () =>
      apiPatch<PlatformPaperDraftDTO>(`/platform/papers/drafts/${detail.id}`, body()),
    onSuccess: (saved) => {
      accept(saved);
      setMessage('Draft saved.');
      setError('');
    },
    onError: fail,
  });

  const publish = useMutation({
    mutationFn: async () => {
      await flush();
      return apiPost<{ paperId: string }>(`/platform/papers/drafts/${detail.id}/publish`, {});
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: LIST_KEY });
      navigate(`/admin/papers/${result.paperId}?kind=paper`);
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => apiDelete(`/platform/papers/drafts/${detail.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LIST_KEY });
      navigate('/admin/papers');
    },
    onError: fail,
  });

  const generate = useMutation({
    mutationFn: async (vars: { index: number; parts: TeamPart[]; mode: 'fill' | 'rewrite' }) => {
      await flush();
      return apiPost<PlatformPaperDraftDTO>(
        `/platform/papers/drafts/${detail.id}/generate-content`,
        vars,
      );
    },
    onSuccess: (saved, vars) => {
      accept(saved);
      setMessage(
        `Q${vars.index}: AI wrote ${vars.parts.map((p) => TEAM_PART_LABEL[p]).join(', ')}. It is marked as a draft until you edit it.`,
      );
      setError('');
    },
    onError: fail,
  });

  const tag = useMutation({
    mutationFn: async (index: number) => {
      await flush();
      return apiPost<PlatformPaperDraftDTO & PlatformTagResult>(
        `/platform/papers/drafts/${detail.id}/tag`,
        { index },
      );
    },
    onSuccess: (result, index) => {
      accept(result);
      setMessage(tagMessage(index, result));
      setError('');
    },
    onError: fail,
  });

  const patchItem = (index: number, patch: Partial<CustomPaperItem>, part?: TeamPart) => {
    setItems((current) =>
      current.map((item) =>
        item.index === index
          ? {
              ...item,
              ...patch,
              aiDrafted: part ? (item.aiDrafted ?? []).filter((p) => p !== part) : item.aiDrafted,
            }
          : item,
      ),
    );
    setDirty(true);
    setMessage('');
  };

  const busy =
    save.isPending || publish.isPending || generate.isPending || tag.isPending || remove.isPending;
  const untagged = items.filter((i) => !concepts.some((c) => c.subtopicId === i.subtopicId));
  const totalMarks = items.reduce((sum, i) => sum + (i.marks ?? 0), 0);

  const confirmPublish = () => {
    const warn = untagged.length
      ? `\n\n${untagged.map((i) => `Q${i.index}`).join(', ')} ${untagged.length === 1 ? 'has' : 'have'} no sub-topic yet — publishing will be refused until each is filed.`
      : '';
    if (
      window.confirm(
        `Publish “${title}” for every school on ${detail.board} · Class ${gradeLabel(detail.grade)}? Students will see it under Past papers.${warn}`,
      )
    ) {
      publish.mutate();
    }
  };

  return (
    <>
      <StudentPageHeader
        breadcrumbs={crumbs(detail.title)}
        title={detail.title}
        subtitle={`${detail.subjectName ?? 'No subject'} · ${detail.board} · Class ${gradeLabel(detail.grade)} · ${detail.source === 'UPLOAD' ? `from ${detail.fileName ?? 'a PDF'}` : 'typed in'}`}
        action={<StatePill state={detail.status} />}
      />

      {detail.status === 'processing' && (
        <Card>
          <div className="flex items-center gap-3 text-sm text-ink-soft">
            <span className="h-5 w-5 flex-none animate-spin rounded-full border-2 border-line border-t-forest" />
            AI is reading the paper, solving each question and filing it under a sub-topic. This
            page updates by itself.
          </div>
        </Card>
      )}
      {detail.status === 'failed' && (
        <Card>
          <p className="text-sm text-red-700">{detail.error ?? 'This paper could not be read.'}</p>
          <p className="mt-1 text-xs text-ink-soft">
            The draft is kept. Add the questions below by hand and save — it becomes a typed-in
            paper.
          </p>
        </Card>
      )}

      {editable && (
        <Card>
          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <Field label="Title">
              {({ id }) => (
                <Input
                  id={id}
                  value={title}
                  maxLength={200}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setDirty(true);
                  }}
                />
              )}
            </Field>
            <Field label="Exam year">
              {({ id }) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  value={year}
                  onChange={(e) => {
                    setYear(e.target.value.replace(/\D/g, '').slice(0, 4));
                    setDirty(true);
                  }}
                />
              )}
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <p className="mr-auto text-xs text-ink-soft">
              {items.length} question{items.length === 1 ? '' : 's'} · {totalMarks} marks · about{' '}
              {Math.round(paperSeconds(items) / 60)} min
              {untagged.length > 0 && ` · ${untagged.length} without a sub-topic`}
            </p>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setItems((current) => [...current, defaultItem(current.length + 1)]);
                setDirty(true);
              }}
            >
              Add question
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!dirty || busy}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save draft'}
            </Button>
            <Button
              size="sm"
              disabled={busy || items.length === 0 || !reviewable}
              onClick={confirmPublish}
            >
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Delete the draft “${detail.title}”? This cannot be undone.`)) {
                  remove.mutate();
                }
              }}
            >
              Delete draft
            </Button>
          </div>
        </Card>
      )}

      {message && <p className="text-sm text-green-700">{message}</p>}
      {error && (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      {editable && items.length === 0 && (
        <Card>
          <p className="text-sm text-ink-soft">No questions yet. Add the first one.</p>
        </Card>
      )}
      {editable &&
        items.map((item) => (
          <PaperItemEditor
            key={item.index}
            idPrefix={detail.id}
            item={item}
            concepts={concepts}
            editable={editable}
            busy={busy}
            generating={generate.isPending && generate.variables?.index === item.index}
            tagging={tag.isPending && tag.variables === item.index}
            retagLabel="↻ Re-tag with AI"
            onPatch={patchItem}
            onTypeChange={(index: number, type: PaperQuestionType) =>
              patchItem(index, {
                type,
                options: type === 'MCQ' ? ['', '', '', ''] : null,
                correctOptionIdx: null,
              })
            }
            onGenerate={(parts, mode) => {
              if (!reviewable) return setError('Save the draft first.');
              generate.mutate({ index: item.index, parts, mode });
            }}
            onTag={() => {
              if (!reviewable) return setError('Save the draft first.');
              tag.mutate(item.index);
            }}
            onRemove={() => {
              setItems((current) =>
                current
                  .filter((row) => row.index !== item.index)
                  .map((row, i) => ({ ...row, index: i + 1 })),
              );
              setDirty(true);
            }}
          />
        ))}
    </>
  );
}

// ─────────────────────────── a published paper ───────────────────────────

/** Hand the browser a JSON document to save. */
function download(fileName: string, value: unknown) {
  const blob = new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function PublishedEditor({ paperId }: { paperId: string }) {
  const paper = useQuery({
    queryKey: ['platform', 'paper', paperId],
    queryFn: () => apiGet<PublishedPaperDTO>(`/platform/papers/published/${paperId}`),
  });
  return (
    <StudentPage>
      <QueryState isPending={paper.isPending} error={paper.error}>
        {paper.data && <PublishedReview key={paper.data.id} paper={paper.data} />}
      </QueryState>
    </StudentPage>
  );
}

function PublishedReview({ paper }: { paper: PublishedPaperDTO }) {
  const queryKey = ['platform', 'paper', paper.id];
  const [items, setItems] = useState<PublishedPaperItem[]>(paper.items);
  const [dirty, setDirty] = useState<Set<number>>(new Set());
  const [keyOpen, setKeyOpen] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const concepts = useConcepts(paper.subjectId);

  const fail = (err: unknown) => {
    setError(apiErrorMessage(err));
    setMessage('');
  };
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: LIST_KEY });
  };
  const replace = (index: number, next: PublishedPaperItem) =>
    setItems((current) => current.map((row) => (row.index === index ? next : row)));
  const markDirty = (index: number, on: boolean) =>
    setDirty((current) => {
      const next = new Set(current);
      if (on) next.add(index);
      else next.delete(index);
      return next;
    });

  const saveRow = useMutation({
    mutationFn: (item: PublishedPaperItem) => {
      const { questionId, ...row } = item;
      const [clean] = serializeItems([row]);
      return apiPatch<{ item: PublishedPaperItem; changed: string[] }>(
        `/platform/papers/published/${paper.id}/questions/${questionId}`,
        { item: { ...clean, index: 1 }, confirmKeyChange: keyOpen.has(item.index) },
      );
    },
    onSuccess: (result, item) => {
      replace(item.index, { ...result.item, index: item.index });
      markDirty(item.index, false);
      setKeyOpen((current) => {
        const next = new Set(current);
        next.delete(item.index);
        return next;
      });
      setMessage(
        result.changed.length
          ? `Q${item.index} saved: ${result.changed.join(', ')}.`
          : `Q${item.index}: nothing had changed.`,
      );
      setError('');
      refresh();
    },
    onError: fail,
  });

  const team = useMutation({
    mutationFn: (vars: {
      item: PublishedPaperItem;
      parts: TeamPart[];
      mode: 'fill' | 'rewrite';
    }) => {
      const { questionId: _id, ...row } = vars.item;
      const [clean] = serializeItems([row]);
      return apiPost<CustomPaperItem>('/platform/papers/team-draft', {
        item: { ...clean, index: 1 },
        subjectId: paper.subjectId,
        parts: vars.parts,
        mode: vars.mode,
      });
    },
    onSuccess: (drafted, vars) => {
      replace(vars.item.index, { ...vars.item, ...drafted, index: vars.item.index });
      markDirty(vars.item.index, true);
      setMessage(
        `Q${vars.item.index}: AI drafted ${vars.parts.map((p) => TEAM_PART_LABEL[p]).join(', ')}. Read it, then save the question.`,
      );
      setError('');
    },
    onError: fail,
  });

  const tag = useMutation({
    mutationFn: (item: PublishedPaperItem) =>
      apiPost<PlatformTagResult>('/platform/papers/tag', {
        subjectId: paper.subjectId,
        question: item.question,
        options: item.options,
      }),
    onSuccess: (result, item) => {
      if (result.tag) {
        replace(item.index, {
          ...item,
          subtopicId: result.tag.subtopicId,
          concept: result.tag.name,
        });
        markDirty(item.index, true);
      }
      setMessage(
        `${tagMessage(item.index, result)}${result.tag ? ' Save the question to keep it.' : ''}`,
      );
      setError('');
    },
    onError: fail,
  });

  const withdraw = useMutation({
    mutationFn: (restore: boolean) =>
      apiPost(`/platform/papers/published/${paper.id}/${restore ? 'restore' : 'withdraw'}`, {}),
    onSuccess: () => {
      setError('');
      refresh();
    },
    onError: fail,
  });

  const helpDownload = useMutation({
    mutationFn: () => apiGet<PaperHelpContent>(`/platform/papers/published/${paper.id}/help`),
    onSuccess: (content) => download(`${paper.id}.paperhelp.json`, content),
    onError: fail,
  });

  const helpUpload = useMutation({
    mutationFn: (input: { fileName: string; content: unknown }) =>
      apiPut<PaperHelpUploadResult>(`/platform/papers/published/${paper.id}/help`, input),
    onSuccess: async (result) => {
      setMessage(
        `Help file applied: ${result.updated} question${result.updated === 1 ? '' : 's'} changed, ${result.unchanged} already matched.`,
      );
      setError('');
      // The rows on screen are now behind; reload them unless one is mid-edit.
      const fresh = await apiGet<PublishedPaperDTO>(`/platform/papers/published/${paper.id}`);
      setItems((current) =>
        current.map((row) =>
          dirty.has(row.index) ? row : (fresh.items.find((f) => f.index === row.index) ?? row),
        ),
      );
      refresh();
    },
    onError: fail,
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    if (file.size > PAPER_HELP_MAX_BYTES) {
      return setError(
        `That file is ${Math.ceil(file.size / 1024)} KB — a help file can be at most ${PAPER_HELP_MAX_BYTES / 1024} KB.`,
      );
    }
    let content: unknown;
    try {
      content = JSON.parse(await file.text());
    } catch {
      return setError(`${file.name} is not valid JSON. Check for a missing comma or quote.`);
    }
    helpUpload.mutate({ fileName: file.name, content });
  };

  const withdrawn = Boolean(paper.withdrawnAt);
  const busy = saveRow.isPending || team.isPending || tag.isPending || helpUpload.isPending;

  return (
    <>
      <StudentPageHeader
        breadcrumbs={crumbs(paper.title)}
        title={paper.title}
        subtitle={`${paper.subjectName} · ${paper.board} · Class ${gradeLabel(paper.grade)}${paper.examYear ? ` · ${paper.examYear}` : ''} · ${paper.items.length} questions · ${paper.totalMarks} marks · ${paper.sittings} sitting${paper.sittings === 1 ? '' : 's'}`}
        action={<StatePill state={withdrawn ? 'withdrawn' : 'published'} />}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <p className="mr-auto max-w-xl text-xs text-ink-soft">
            {withdrawn
              ? 'Withdrawn: off every student’s list and cannot be started. Sittings already made, and their reports, still work.'
              : 'Live for every school on this board and grade. Hints, explanation, mistakes, video and the sub-topic can be edited at any time; the question and its key are held still.'}
          </p>
          <Button
            size="sm"
            variant="secondary"
            disabled={helpDownload.isPending}
            onClick={() => helpDownload.mutate()}
          >
            Download help JSON
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(event) => {
              void onFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={helpUpload.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {helpUpload.isPending ? 'Checking…' : 'Upload help JSON'}
          </Button>
          <Button
            size="sm"
            variant={withdrawn ? 'primary' : 'secondary'}
            disabled={withdraw.isPending}
            onClick={() => {
              const ask = withdrawn
                ? 'Restore this paper? It returns to every student’s past papers.'
                : 'Withdraw this paper? It leaves every student’s list and cannot be started. Sittings already made, and their reports, stay.';
              if (window.confirm(ask)) withdraw.mutate(withdrawn);
            }}
          >
            {withdrawn ? 'Restore' : 'Withdraw'}
          </Button>
        </div>
      </Card>

      {message && <p className="text-sm text-green-700">{message}</p>}
      {error && (
        <p className="whitespace-pre-line text-sm" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      {items.map((item) => {
        const open = keyOpen.has(item.index);
        return (
          <div key={item.questionId} className="flex flex-col gap-2">
            <PaperItemEditor
              idPrefix={paper.id}
              item={item}
              concepts={concepts}
              editable
              busy={busy}
              lockKey={!open}
              lockShape
              generating={team.isPending && team.variables?.item.index === item.index}
              tagging={tag.isPending && tag.variables?.index === item.index}
              retagLabel="↻ Re-tag with AI"
              onPatch={(index, patch, part) => {
                replace(index, {
                  ...item,
                  ...patch,
                  aiDrafted: part
                    ? (item.aiDrafted ?? []).filter((p) => p !== part)
                    : item.aiDrafted,
                });
                markDirty(index, true);
                setMessage('');
              }}
              onTypeChange={() => undefined}
              // The Answer part is the key: AI may draft it only once the key is open.
              onGenerate={(parts, mode) => {
                const allowed = open ? parts : parts.filter((p) => p !== 'A');
                if (allowed.length === 0)
                  return setError('Open the answer key to redraft the Answer.');
                team.mutate({ item, parts: allowed, mode });
              }}
              onTag={() => tag.mutate(item)}
            />
            <div className="flex flex-wrap items-center justify-end gap-2">
              {!open && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    const ask =
                      paper.sittings > 0
                        ? `Students have already sat this paper ${paper.sittings} time${paper.sittings === 1 ? '' : 's'}. Their recorded results will NOT be re-marked if you change Q${item.index}'s question or answer key. Change it anyway?`
                        : `Change Q${item.index}'s question or answer key?`;
                    if (window.confirm(ask))
                      setKeyOpen((current) => new Set(current).add(item.index));
                  }}
                >
                  Change answer key
                </Button>
              )}
              <Button
                size="sm"
                disabled={busy || !dirty.has(item.index)}
                onClick={() => saveRow.mutate(item)}
              >
                {saveRow.isPending && saveRow.variables?.index === item.index
                  ? 'Saving…'
                  : `Save Q${item.index}`}
              </Button>
            </div>
          </div>
        );
      })}
    </>
  );
}
